import type { Location } from 'react-router';
import { generatePath, matchPath } from 'react-router';

import { UserRole } from 'src/api';
import { ReactRouteAccessKey } from 'src/constants/reactRouter';
import { BusinessRole, UserPermissionKey, UserPermissions } from 'src/constants/user';
import type { RouteProps } from 'src/types/router';

const mappedRolesToObject: [objectKey: UserPermissionKey, systemRoleName: BusinessRole][] = [
  [UserPermissionKey.isPlatformAdmin, BusinessRole.PlatformAdmin],
  [UserPermissionKey.isPlatformManager, BusinessRole.PlatformManager],
  [UserPermissionKey.isEmployerAdmin, BusinessRole.EmployerAdmin],
  [UserPermissionKey.isEmployerManager, BusinessRole.EmployerManager],
  [UserPermissionKey.isVerifierAdmin, BusinessRole.VerifierAdmin],
  [UserPermissionKey.isVerifierManager, BusinessRole.VerifierManager],
  [UserPermissionKey.isEmployee, BusinessRole.Employee],
  [UserPermissionKey.isPeoManager, BusinessRole.PeoManager],
  [UserPermissionKey.isPeoAdmin, BusinessRole.PeoAdmin],
  [UserPermissionKey.isPeoSuperAdmin, BusinessRole.PeoSuperAdmin],
  [UserPermissionKey.isPeoPayrollProviderUser, BusinessRole.PeoPayrollProviderUser],
];

type RoutesTreeCheckerFnCustomConditions = {
  isPartnershipCompanyUser: boolean;
};

// Custom condition keys that require AND logic (must all be satisfied)
const customConditionKeys: string[] = [
  ReactRouteAccessKey.isPartnershipCompanyUser,
  ReactRouteAccessKey.isNotPartnershipCompanyUser,
];

export const routesTreeChecker = (
  routesTree: RouteProps[],
  isAuthorized: boolean,
  userPermissions: UserPermissions,
  customConditions: RoutesTreeCheckerFnCustomConditions,
): RouteProps[] => {
  const checkCustomCondition = (conditionKey: string): boolean => {
    if (conditionKey === ReactRouteAccessKey.isPartnershipCompanyUser) {
      return customConditions.isPartnershipCompanyUser;
    }

    if (conditionKey === ReactRouteAccessKey.isNotPartnershipCompanyUser) {
      return !customConditions.isPartnershipCompanyUser;
    }

    return false;
  };

  const checkRolePermission = (accessRole: string): boolean => {
    for (const [objectKey, systemRoleName] of mappedRolesToObject) {
      if (accessRole === systemRoleName && userPermissions[objectKey]) {
        return true;
      }
    }

    if (accessRole === ReactRouteAccessKey.hasPlatformRoles && userPermissions.hasPlatformRoles) {
      return true;
    }

    if (accessRole === ReactRouteAccessKey.isPlatformAdmin && userPermissions.isPlatformAdmin) {
      return true;
    }

    if (accessRole === ReactRouteAccessKey.isPlatformManager && userPermissions.isPlatformManager) {
      return true;
    }

    if (accessRole === ReactRouteAccessKey.hasEmployerRoles && userPermissions.hasEmployerRoles) {
      return true;
    }

    if (accessRole === ReactRouteAccessKey.hasVerifierRoles && userPermissions.hasVerifierRoles) {
      return true;
    }

    if (accessRole === ReactRouteAccessKey.isEmployee && userPermissions.isEmployee) {
      return true;
    }

    if (accessRole === ReactRouteAccessKey.hasPeoRoles && userPermissions.hasPeoRoles) {
      return true;
    }

    if (
      accessRole === ReactRouteAccessKey.isPeoPayrollProviderUser &&
      userPermissions.isPeoPayrollProviderUser
    ) {
      return true;
    }

    if (accessRole === ReactRouteAccessKey.anyAuthorized && isAuthorized) {
      return true;
    }

    if (accessRole === UserRole.Guest) {
      return true;
    }

    return false;
  };

  const checkPermission = (accessRoles: string[] = []): boolean => {
    // Separate roles and custom conditions
    const roles = accessRoles.filter(role => !customConditionKeys.includes(role));
    const conditions = accessRoles.filter(role => customConditionKeys.includes(role));

    // Check roles (OR logic) - at least one role must match
    // If no roles specified, consider it as passed
    const hasRolePermission = roles.length === 0 || roles.some(checkRolePermission);

    // Check custom conditions (AND logic) - all conditions must be satisfied
    const hasConditionsPermission = conditions.every(checkCustomCondition);

    return hasRolePermission && hasConditionsPermission;
  };

  const routesTreeWalker = (
    routesTree: RouteProps[] = [],
    parentPermissions: string[] = [],
  ): RouteProps[] => {
    return routesTree.map(route => {
      let accessPermissions = route.accessRoles || [];
      const needToInheritPermissions = accessPermissions.includes(ReactRouteAccessKey.inherit);

      if (needToInheritPermissions) {
        accessPermissions = [
          ...parentPermissions,
          ...accessPermissions.filter(role => role !== ReactRouteAccessKey.inherit),
        ];
      }

      const hasPermission = checkPermission(accessPermissions);

      return {
        ...route,
        element: hasPermission ? route.element : null,
        childRoutes: routesTreeWalker(route.childRoutes, accessPermissions),
      };
    });
  };

  return routesTreeWalker(routesTree, []);
};

export type Params<Key extends string = string> = {
  readonly [key in Key]: string | undefined;
};
export type PathParams = Params;
export type QueryParams = Record<string, any>;

/**
 * Build url with query and path params.
 *
 * @example
 * buildUrl('/products/:id', { id: 123 }, { filter: 'someFilter' }) // "/products/123?filter=someFilter"
 */
export const buildUrl = (path: string, pathParams?: PathParams, queryParams?: QueryParams) => {
  let query = '';
  if (queryParams) {
    const searchParams = new URLSearchParams(queryParams);
    query = searchParams.size > 0 ? `?${searchParams.toString()}` : '';
  }

  return `${generatePath(path, pathParams)}${query}`;
};

export const getIsPathMatchLocation = (location: Location, path: string) => {
  return !!matchPath({ path, end: false }, location.pathname);
};
